import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import * as QRCode from 'qrcode';
import { environment } from '../../../../environments/environment';
import {
  LucideAngularModule,
  RefreshCw,
  Plus,
  X,
  Smartphone,
  BatteryLow,
  BatteryMedium,
  BatteryFull,
  Wifi,
  WifiOff,
  Copy,
  Check,
  Power,
  Send,
  Pencil,
  Link,
} from 'lucide-angular';
import {
  NotificationSettingsService,
  NotificationChannelMode,
} from '../../../core/services/notification-settings.service';
import {
  SmsGatewayService,
  SmsGatewayDto,
  CreateSmsGatewayResult,
} from '../../../core/services/sms-gateway.service';

interface ModeOption {
  value: NotificationChannelMode;
  title: string;
  description: string;
}

@Component({
  selector: 'app-admin-sms-gateway',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, ConfirmDialogModule],
  providers: [ConfirmationService],
  templateUrl: './sms-gateway.component.html',
})
export class AdminSmsGatewayComponent implements OnInit {
  private settingsSvc = inject(NotificationSettingsService);
  private gatewaySvc = inject(SmsGatewayService);
  private confirmSvc = inject(ConfirmationService);

  RefreshCw = RefreshCw;
  Plus = Plus;
  X = X;
  Smartphone = Smartphone;
  BatteryLow = BatteryLow;
  BatteryMedium = BatteryMedium;
  BatteryFull = BatteryFull;
  Wifi = Wifi;
  WifiOff = WifiOff;
  Copy = Copy;
  Check = Check;
  Power = Power;
  Send = Send;
  Pencil = Pencil;
  Link = Link;

  // Diqqat: bu rejim parol tiklash kodiga (OTP) TA'SIR QILMAYDI — OTP doim
  // Telegram bot orqali ketadi (ishonchlilik uchun, SMS-gateway'ga bog'liq
  // emas). Bu yerdagi tanlov hozircha kelajakdagi xabar turlari (qarz
  // eslatmasi, tizim ogohlantirishi, marketing) uchun tayyorlangan — ular
  // hali ulanmagan.
  readonly modeOptions: ModeOption[] = [
    {
      value: 'TelegramOnly',
      title: 'Faqat Telegram',
      description:
        'Boshqa xabar turlari (qarz eslatmasi va h.k.) faqat Telegram bot orqali ketadi.',
    },
    {
      value: 'SmsOnly',
      title: 'Faqat SMS',
      description: "Boshqa xabar turlari Telegram ulangan bo'lsa ham SMS-gateway orqali ketadi.",
    },
    {
      value: 'Hybrid',
      title: 'Gibrid (tavsiya etiladi)',
      description: "Avval Telegram; botga ulanmagan bo'lsa SMS-gateway orqali yuboriladi.",
    },
  ];

  mode = signal<NotificationChannelMode>('Hybrid');
  modeLoading = signal(false);
  modeSaving = signal(false);

  gateways = signal<SmsGatewayDto[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);

  createOpen = signal(false);
  createSubmitting = signal(false);
  createName = signal('');
  createDailyLimit = signal(200);

  result = signal<CreateSmsGatewayResult | null>(null);
  qrDataUrl = signal<string | null>(null);
  // Admin panel qaysi API'ga ulangan bo'lsa, qurilma ham o'shanga ulanishi
  // kerak — standart qiymat shundan, lekin boshqa muhitga o'rnatilayotgan
  // qurilma uchun tahrirlanadi.
  serverUrl = signal(environment.apiUrl);
  codeCopied = signal(false);

  togglingId = signal<string | null>(null);
  reconnectingId = signal<string | null>(null);

  editOpen = signal(false);
  editSubmitting = signal(false);
  editTarget: SmsGatewayDto | null = null;
  editName = signal('');
  editDailyLimit = signal(200);
  editSimPhoneNumber = signal('');

  testPhone = signal('');
  testText = signal('');
  testSending = signal(false);
  testResult = signal<'ok' | 'error' | null>(null);
  testError = signal<string | null>(null);

  ngOnInit(): void {
    this.loadMode();
    this.loadGateways();
  }

  loadMode(): void {
    this.modeLoading.set(true);
    this.settingsSvc.get().subscribe({
      next: (res) => {
        if (res.data) this.mode.set(res.data.notificationChannelMode);
        this.modeLoading.set(false);
      },
      error: () => this.modeLoading.set(false),
    });
  }

  selectMode(value: NotificationChannelMode): void {
    if (value === this.mode() || this.modeSaving()) return;

    const previous = this.mode();
    this.mode.set(value);
    this.modeSaving.set(true);

    this.settingsSvc.update(value).subscribe({
      next: (res) => {
        if (res.data) this.mode.set(res.data.notificationChannelMode);
        this.modeSaving.set(false);
      },
      error: () => {
        // Muvaffaqiyatsiz bo'lsa eski qiymatga qaytamiz — ekran serverdagi
        // holatdan ko'ra optimistik bo'lib qolmasin.
        this.mode.set(previous);
        this.modeSaving.set(false);
      },
    });
  }

  loadGateways(): void {
    this.loading.set(true);
    this.error.set(null);
    this.gatewaySvc.getAll().subscribe({
      next: (res) => {
        this.gateways.set(res.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.error.set("Ro'yxatni yuklab bo'lmadi");
        this.loading.set(false);
      },
    });
  }

  openCreate(): void {
    this.createName.set('');
    this.createDailyLimit.set(200);
    this.result.set(null);
    this.qrDataUrl.set(null);
    this.createOpen.set(true);
  }

  closeCreate(): void {
    this.createOpen.set(false);
  }

  submitCreate(): void {
    const name = this.createName().trim();
    if (!name || this.createSubmitting()) return;

    this.createSubmitting.set(true);
    this.gatewaySvc.create(name, this.createDailyLimit()).subscribe({
      next: async (res) => {
        this.createSubmitting.set(false);
        if (!res.data) return;

        this.result.set(res.data);
        await this.renderQr(res.data.activationCode);
        this.loadGateways();
      },
      error: () => {
        this.createSubmitting.set(false);
      },
    });
  }

  /**
   * QR ichiga URI qo'yamiz (JSON emas) — gateway ilovasi uni bitta
   * `Uri.TryCreate` bilan o'qiydi, server manzili va kodni alohida
   * so'ramaydi.
   */
  private async renderQr(code: string): Promise<void> {
    const url = new URL('miyzan-sms://activate');
    url.searchParams.set('baseUrl', this.serverUrl());
    url.searchParams.set('code', code);

    this.qrDataUrl.set(await QRCode.toDataURL(url.toString(), { width: 260, margin: 1 }));
  }

  async copyCode(): Promise<void> {
    const code = this.result()?.activationCode;
    if (!code) return;

    await navigator.clipboard.writeText(code);
    this.codeCopied.set(true);
    setTimeout(() => this.codeCopied.set(false), 2000);
  }

  onServerUrlChange(): void {
    const code = this.result()?.activationCode;
    if (code) void this.renderQr(code);
  }

  toggleActive(gw: SmsGatewayDto): void {
    if (this.togglingId()) return;

    const nextActive = !gw.isActive;
    this.togglingId.set(gw.id);

    this.gatewaySvc.setActive(gw.id, nextActive).subscribe({
      next: () => {
        this.gateways.update((list) =>
          list.map((g) => (g.id === gw.id ? { ...g, isActive: nextActive } : g)),
        );
        this.togglingId.set(null);
      },
      error: () => this.togglingId.set(null),
    });
  }

  openEdit(gw: SmsGatewayDto): void {
    this.editTarget = gw;
    this.editName.set(gw.name);
    this.editDailyLimit.set(gw.dailyLimit);
    this.editSimPhoneNumber.set(gw.simPhoneNumber ?? '');
    this.editOpen.set(true);
  }

  closeEdit(): void {
    this.editOpen.set(false);
    this.editTarget = null;
  }

  submitEdit(): void {
    const target = this.editTarget;
    const name = this.editName().trim();
    if (!target || !name || this.editSubmitting()) return;

    this.editSubmitting.set(true);
    this.gatewaySvc
      .update(target.id, name, this.editDailyLimit(), this.editSimPhoneNumber())
      .subscribe({
        next: () => {
          this.editSubmitting.set(false);
          this.closeEdit();
          this.loadGateways();
        },
        error: () => this.editSubmitting.set(false),
      });
  }

  /**
   * Qurilmani qayta ulaydi — telefon almashtirilganda yoki ilova qayta
   * o'rnatilib, kalit yo'qolganda. Eski kalitni bekor qilgani uchun avval
   * tasdiqlatamiz: hozir ulangan telefon bo'lsa, u shu amaldan keyin ishlashni
   * to'xtatadi.
   */
  reconnect(gw: SmsGatewayDto): void {
    this.confirmSvc.confirm({
      header: 'Qayta ulash',
      icon: 'pi pi-exclamation-triangle',
      message: `"${gw.name}" uchun yangi faollashtirish kodi chiqariladi va eski kalit bekor qilinadi. Qurilma yangi QR/kod bilan qayta skanerlanmaguncha xabar yubora olmaydi. Davom etaymi?`,
      acceptLabel: 'Ha, qayta ulash',
      rejectLabel: 'Bekor qilish',
      accept: () => this.doReconnect(gw),
    });
  }

  private doReconnect(gw: SmsGatewayDto): void {
    if (this.reconnectingId()) return;
    this.reconnectingId.set(gw.id);

    this.gatewaySvc.reconnect(gw.id).subscribe({
      next: async (res) => {
        this.reconnectingId.set(null);
        if (!res.data) return;

        this.result.set(res.data);
        this.qrDataUrl.set(null);
        this.createOpen.set(true);
        await this.renderQr(res.data.activationCode);
        this.loadGateways();
      },
      error: () => this.reconnectingId.set(null),
    });
  }

  sendTestSms(): void {
    const phone = this.testPhone().trim();
    if (!phone || this.testSending()) return;

    this.testSending.set(true);
    this.testResult.set(null);
    this.testError.set(null);

    this.gatewaySvc.sendTest(phone, this.testText()).subscribe({
      next: () => {
        this.testSending.set(false);
        this.testResult.set('ok');
      },
      error: (err) => {
        this.testSending.set(false);
        this.testResult.set('error');
        this.testError.set(this.extractError(err) || "Yuborib bo'lmadi.");
      },
    });
  }

  private extractError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object') {
        const errors = (body as { errors?: string[] }).errors;
        if (Array.isArray(errors) && errors.length) return errors[0];
        const msg = (body as { message?: string }).message;
        if (typeof msg === 'string' && msg) return msg;
      }
    }
    return '';
  }

  batteryIcon(level: number | null) {
    if (level === null) return this.BatteryMedium;
    if (level <= 20) return this.BatteryLow;
    if (level <= 70) return this.BatteryMedium;
    return this.BatteryFull;
  }

  lastSeenText(value: string | null): string {
    if (!value) return 'Hech qachon';
    const diffMs = Date.now() - new Date(value).getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'Hozirgina';
    if (minutes < 60) return `${minutes} daqiqa oldin`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} soat oldin`;
    return `${Math.floor(hours / 24)} kun oldin`;
  }
}
